import Button from '@material-ui/core/Button'
import React, {useState, useEffect, useCallback} from 'react'
import {Attendee} from 'Event/attendee'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import {useMarkTechCheckComplete} from 'organization/Event/AttendeesProvider'
import Typography from '@material-ui/core/Typography'
import {spacing} from 'lib/ui/theme'
import withStyles from '@material-ui/core/styles/withStyles'
import {useArea} from 'organization/Event/Area/AreaProvider'
import Box from '@material-ui/core/Box'
import {useOrganization} from 'organization/OrganizationProvider'
import {useEvent} from 'Event/EventProvider'
import {api} from 'lib/url'
import {useRoom} from 'organization/Event/Room/RoomProvider'
import {useInterval} from 'lib/interval'
import {Room} from 'Event/room'
import EditButton from 'lib/ui/Button/CustomButton'
import TechCheckAttendeeUpdateDialog from 'organization/Event/Room/TechCheckAttendeeUpdateDialog'
import {colors} from 'lib/ui/theme'
import {useIfMounted} from 'lib/dom'

/**
 * Delay to wait before actually sending Check In request. This is
 * to fix a user issue where clicking too quick resulted in the
 * wrong attendee being checked in.
 */
const SEND_CHECK_IN_DELAY_MS = 2000

export default function TechCheckAttendees() {
  const {area} = useArea()

  if (!area.is_tech_check) {
    return null
  }

  return <Content />
}

function Content() {
  const assignments = useTechCheckAssignments()
  const [attendees, setAttendees] = useState<Attendee[]>([])
  const checkIn = useMarkTechCheckComplete()
  const [error, setError] = useState('')
  const [editing, setEditing] = useState<Attendee | null>(null)
  const edit = (attendee: Attendee) => () => setEditing(attendee)

  const ifMounted = useIfMounted()

  const stopEditing = () => {
    setEditing(null)
  }

  useEffect(() => {
    const attendees = assignments.map(({attendee}) => attendee)
    setAttendees(attendees)
  }, [assignments])

  const handleCheckIn = (attendee: Attendee) => {
    setError('')

    checkIn(attendee)
      .then(() => {
        // Only remove attendee after a delay
        setTimeout(
          ifMounted(() => {
            const updated = attendees.filter((item) => item.id !== attendee.id)
            setAttendees(updated)
          }),
          SEND_CHECK_IN_DELAY_MS,
        )
      })
      .catch((e) => {
        setError(e.message)
      })
  }

  const update = (target: Attendee) => {
    setAttendees((current) => {
      const updated = current.map((c) => {
        if (c.id === target.id) {
          return target
        }
        return c
      })
      return updated
    })
  }

  return (
    <>
      <TechCheckAttendeeUpdateDialog
        attendee={editing}
        onClose={stopEditing}
        update={update}
      />
      <Box mt={3}>
        <Box mt={2}>
          <Typography variant="h6">Attendees</Typography>
        </Box>
        <Error>{error}</Error>
        <Attendees
          attendees={attendees}
          checkIn={handleCheckIn}
          onSelectEdit={edit}
        />
      </Box>
    </>
  )
}

function Attendees(props: {
  attendees: Attendee[]
  checkIn: (attendee: Attendee) => void
  onSelectEdit: (attendee: Attendee) => () => void
}) {
  const {attendees, onSelectEdit, checkIn} = props
  const hasAttendees = attendees.length > 0

  if (!hasAttendees) {
    return <Typography>No attendees assigned to this room</Typography>
  }

  return (
    <Table size="small" aria-label="tech check assignments table">
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell>Email</TableCell>
          <TableCell></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {props.attendees.map((attendee) => (
          <TableRow key={attendee.id}>
            <TableCell component="th" scope="row" aria-label="name">
              <EditButton
                variant="text"
                onClick={onSelectEdit(attendee)}
                textColor={colors.primary}
                aria-label="edit"
              >
                {`${attendee.first_name} ${attendee.last_name}`}
              </EditButton>
            </TableCell>
            <TableCell>{attendee.email}</TableCell>
            <TableCell>
              <CheckInButton attendee={attendee} checkIn={checkIn} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function CheckInButton(props: {
  attendee: Attendee
  checkIn: (attendee: Attendee) => void
}) {
  const {attendee, checkIn} = props
  const [processing, setProcessing] = useState(false)

  const handleCheckIn = () => {
    setProcessing(true)
    checkIn(attendee)
  }

  return (
    <Button
      variant="contained"
      color="primary"
      aria-label="check in button"
      onClick={handleCheckIn}
      disabled={processing}
    >
      Check-In
    </Button>
  )
}

const POLL_TECH_CHECK_ATTENDEES_INTERVAL_MS = 20000

export interface TechCheckAssignment {
  attendee: Attendee
  room: Room
}

export function useTechCheckAssignments() {
  const {client} = useOrganization()
  const {event} = useEvent()
  const {area} = useArea()
  const {room} = useRoom()

  const [assignments, setAssignments] = useState<TechCheckAssignment[]>([])
  const [shouldAutoRefresh, setShouldAutoRefresh] = useState(true)

  useEffect(() => {
    setShouldAutoRefresh(room.is_online)
  }, [room])

  const fetch = useCallback(() => {
    if (!shouldAutoRefresh) {
      return
    }

    const url = api(
      `/events/${event.id}/areas/${area.id}/room/${room.id}/tech_check_assignments`,
    )
    return client.get<TechCheckAssignment[]>(url).then(setAssignments)
  }, [area, event, client, room, shouldAutoRefresh])

  useEffect(() => {
    fetch()
  }, [fetch])

  useInterval(fetch, POLL_TECH_CHECK_ATTENDEES_INTERVAL_MS)

  return assignments
}

function Error(props: {children: string | null}) {
  if (!props.children) {
    return null
  }

  return <ErrorText color="error">{props.children}</ErrorText>
}

const ErrorText = withStyles({
  root: {
    marginBottom: spacing[3],
  },
})(Typography)
